import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop component that scrolls the window to the top
 * whenever the route changes.
 * 
 * This component:
 * 1. Disables the browser's native scroll restoration
 * 2. Forces scroll to top on every pathname change
 * 3. Uses multiple methods for cross-browser compatibility
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  // Disable browser's native scroll restoration on mount
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      // Reset to auto when component unmounts (optional)
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Scroll to top when pathname changes
  useEffect(() => {
    // Force scroll to top using multiple methods
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }
    };

    // Execute immediately
    scrollToTop();

    // Execute after a microtask (for any async rendering)
    queueMicrotask(scrollToTop);

    // Execute after animation frame
    requestAnimationFrame(scrollToTop);

    // Execute with delays for any lazy-loaded content
    const timers = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
    ];

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [pathname]);

  return null;
};

export default ScrollToTop;
